/**
 * Rounding Utilities (Frontend)
 *
 * Mirrors backend moneyUtils.roundWithConfig for client-side rounding preview
 * when the agent selects Cash or Debit payment in the POS.
 *
 * @module roundingUtils
 */

const ROUNDING_UNIT_MAP = {
  PENNY: 0.01,
  NICKEL: 0.05,
  DIME: 0.10,
  QUARTER: 0.25,
  HALF_DOLLAR: 0.50,
  DOLLAR: 1.00
};

/**
 * Round a monetary value using a configurable unit and direction.
 *
 * @param {number} value - The value to round (in dollars)
 * @param {string} [unitStr='PENNY'] - PENNY, NICKEL, DIME, QUARTER, HALF_DOLLAR, DOLLAR
 * @param {string} [directionStr='nearest'] - up, down, nearest, half_down, bankers
 * @returns {number} Rounded value
 */
export function roundWithConfig(value, unitStr = 'PENNY', directionStr = 'nearest') {
  if (value === null || value === undefined || isNaN(value)) {
    return 0;
  }

  const step = ROUNDING_UNIT_MAP[unitStr] || 0.01;
  const multiplier = 1 / step;
  const scaled = value * multiplier;

  let rounded;
  switch (directionStr) {
    case 'up':
      rounded = Math.ceil(scaled);
      break;
    case 'down':
      rounded = Math.floor(scaled);
      break;
    case 'half_down': {
      const floored = Math.floor(scaled);
      const remainder = scaled - floored;
      rounded = remainder > 0.5 ? Math.ceil(scaled) : floored;
      break;
    }
    case 'bankers': {
      const floored = Math.floor(scaled);
      const remainder = scaled - floored;
      if (Math.abs(remainder - 0.5) < 1e-9) {
        rounded = floored % 2 === 0 ? floored : floored + 1;
      } else {
        rounded = Math.round(scaled);
      }
      break;
    }
    case 'nearest':
    default:
      rounded = Math.round(scaled);
      break;
  }

  return Number((rounded / multiplier).toFixed(2));
}

/**
 * Calculate a rounding preview for the payment screen.
 * Returns the rounded order total and the adjustment amount.
 *
 * @param {number} orderTotal - Current order total in dollars
 * @param {string} roundingUnit - Store rounding unit setting
 * @param {string} roundingDirection - Store rounding direction setting
 * @returns {{ roundedTotal: number, adjustment: number }}
 */
export function calculateRoundingPreview(orderTotal, roundingUnit, roundingDirection) {
  const roundedTotal = roundWithConfig(orderTotal, roundingUnit, roundingDirection);
  const adjustment = Number((roundedTotal - orderTotal).toFixed(2));
  return { roundedTotal, adjustment };
}
